import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { Controller, useForm, useWatch, type Resolver, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiErrorCode, CreateOfferSchema, parseApiError } from '@ooh/contracts';
import { toast } from 'sonner';
import type { Client, CreateOffer, SalesUser } from '@ooh/contracts';
import {
  SharingDialog,
  buildQuickAddToastMessage,
  useAddUnitsToOffer,
  useCreateOffer,
  warnOnAddConflicts,
  resolveSalespersonGating,
  toSalesUserOptions,
  useSalespersonSelfDefault,
  applyAutoSelectedBrand,
  isBrandStillValid,
  useInlineClientCreate,
  computeCampaignNumberPreviewInput,
  BrandField,
  CampaignNumberPreviewField,
  CoCreatorsCheckbox,
  useInlineClientCreateAction,
} from '@ooh/screens';
import { Button } from '@ooh/ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { Label } from '@ooh/ui/components/label';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { useClientsList } from '../../clients/api';
import { useHasPermission } from '../../../hooks/use-has-permission';
import { useCampaignNumberPreview, useOfferSalesUsers } from '../api';
import { buildDefaultValuesFromSearch } from './build-create-offer-defaults';
import { DraftDefaultsSection } from './draft-defaults-section';

interface CreateOfferPageProps {
  canCreate?: boolean;
  /** Whether the current user has the client.create permission. */
  canCreateClient?: boolean;
}

export type CreateOfferFormValues = CreateOffer;

/**
 * Extracted to a standalone function (rather than an inline `auth.user?.id`
 * expression) purely to keep `CreateOfferFormPage`'s own cyclomatic
 * complexity under the lint threshold — each `?.` inside that function's
 * body counts as a branch.
 */
function extractAuthUserId(auth: { user: { id: number } | null | undefined }): number | null {
  return auth.user?.id ?? null;
}

/**
 * `POST /offers` throws 422 from `assertClientExists` (generic
 * `ApiErrorCode.VALIDATION_ERROR`) or `resolveSalespersonId` (the stable
 * `ApiErrorCode.SALESPERSON_INVALID`) — both field-attributable, routed here.
 * A third 422 cause, `CAMPAIGN_NUMBER_GENERATION_EXHAUSTED`, is handled by
 * the caller before this function runs: it names no field, so it gets a
 * toast instead.
 */
function resolveCreateOffer422Error(errorCode: ApiErrorCode): {
  field: 'clientId' | 'salespersonId';
  errorKey: 'create.errors.clientNotFound' | 'create.errors.salespersonInvalid';
} {
  return errorCode === ApiErrorCode.SALESPERSON_INVALID
    ? { field: 'salespersonId', errorKey: 'create.errors.salespersonInvalid' }
    : { field: 'clientId', errorKey: 'create.errors.clientNotFound' };
}

/**
 * Extracted from `handleCreateOffer`'s catch block purely to keep that
 * function's own cyclomatic complexity under the lint threshold (PR #1128
 * review) — `CAMPAIGN_NUMBER_GENERATION_EXHAUSTED` names no field, so it
 * gets its own toast rather than routing through `resolveCreateOffer422Error`.
 */
function handleCreateOfferError(
  error: unknown,
  offerForm: UseFormReturn<CreateOfferFormValues>,
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
) {
  const parsed = parseApiError(error);
  if (parsed.errorCode === ApiErrorCode.CAMPAIGN_NUMBER_GENERATION_EXHAUSTED) {
    toast.error(t('create.errors.campaignNumberExhausted'));
  } else if (parsed.statusCode === 422) {
    const { field, errorKey } = resolveCreateOffer422Error(parsed.errorCode);
    offerForm.setError(field, { message: t(errorKey) });
  } else {
    toast.error(t('create.errors.saveFailed'));
  }
}

function getOfferFieldErrorMessage(
  field: 'brand' | 'clientId',
  message: string | undefined,
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
) {
  if (field === 'brand') {
    return message ?? t('create.errors.brandRequired');
  }

  return message === t('create.errors.clientNotFound')
    ? message
    : t('create.errors.clientRequired');
}

/**
 * Extracted from `CreateOfferFormPage` purely to keep that function's own
 * line count and cyclomatic complexity under the lint thresholds.
 */
function SalespersonField({
  offerForm,
  salesUsers,
  disabled,
  fieldLocked,
  t,
}: {
  offerForm: UseFormReturn<CreateOfferFormValues>;
  salesUsers: SalesUser[];
  disabled: boolean;
  /** True when the caller is sales-department but lacks offer.assign_other_salesperson. */
  fieldLocked: boolean;
  t: ReturnType<typeof useTranslation<'offers'>>['t'];
}) {
  return (
    <div className="space-y-1.5" data-testid="field-salesperson">
      <Label htmlFor="create-offer-salesperson" required>
        {t('create.fields.salesperson')}
      </Label>
      <DisabledHintTooltip disabled={fieldLocked} hint={t('create.fields.salespersonLockedHint')}>
        <Controller
          control={offerForm.control}
          name="salespersonId"
          render={({ field }) => (
            <SearchableSelectField
              data-testid="create-offer-salesperson"
              disabled={disabled || fieldLocked}
              error={Boolean(offerForm.formState.errors.salespersonId)}
              options={toSalesUserOptions(salesUsers)}
              placeholder={t('create.fields.salespersonPlaceholder')}
              value={field.value != null ? String(field.value) : ''}
              onChange={(value) => {
                field.onChange(value ? Number(value) : undefined);
                offerForm.clearErrors('salespersonId');
              }}
            />
          )}
        />
      </DisabledHintTooltip>
      {offerForm.formState.errors.salespersonId?.message ? (
        <p className="text-xs text-destructive" data-testid="field-salesperson-error">
          {offerForm.formState.errors.salespersonId.message}
        </p>
      ) : null}
    </div>
  );
}

export function CreateOfferPage({
  canCreate = true,
  canCreateClient = true,
}: CreateOfferPageProps) {
  const { t } = useTranslation('offers');

  if (!canCreate) {
    return (
      <div
        data-testid="forbidden-message"
        className="flex h-full items-center justify-center py-20"
      >
        <p className="text-muted-foreground">{t('create.forbidden')}</p>
      </div>
    );
  }

  return <CreateOfferFormPage canCreateClient={canCreateClient} />;
}

/**
 * Best-effort: quick-add media units carried through the "Otwórz pełny
 * formularz" navigation (CU-86cb1nqwv) are added after the offer itself is
 * successfully created. A failure here must not block navigation or look
 * like offer creation failed — the offer already exists, so we warn instead.
 *
 * `addUnitsToOffer` can also resolve successfully while reporting some units
 * skipped or conflicted server-side — reuse the same warning helpers
 * `useQuickAddPipeline` uses for that outcome instead of silently dropping
 * the feedback (CU-86cb1nqwv).
 */
async function addQuickAddUnitsBestEffort(
  addUnits: ReturnType<typeof useAddUnitsToOffer>['mutateAsync'],
  offerId: number,
  offerName: string,
  mediaUnitIds: number[] | undefined,
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
  tCommon: ReturnType<typeof useTranslation<'common'>>['t'],
) {
  if (!mediaUnitIds || mediaUnitIds.length === 0) return;

  try {
    const response = await addUnits({ offerId, mediaUnitIds });
    if (response.skippedCount > 0) {
      toast.warning(
        buildQuickAddToastMessage(tCommon, {
          offerId: String(offerId),
          offerName,
          addedCount: response.addedCount,
          skippedCount: response.skippedCount,
          totalCount: response.units.length,
        }),
      );
    }
    warnOnAddConflicts(tCommon, response.conflictedMediaUnitIds);
  } catch (error) {
    console.warn('Failed to add quick-add media units to newly created offer', {
      offerId,
      mediaUnitIds,
      error,
    });
    toast.warning(t('create.errors.addUnitsFailed'));
  }
}

function CreateOfferFormPage({ canCreateClient }: { canCreateClient: boolean }) {
  const { t } = useTranslation('offers');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate({ from: '/offers/new' });
  const search = useSearch({ from: '/offers/new' });
  const { mediaUnitIds } = search;
  const { auth } = useRouteContext({ from: '__root__' });
  const clientsQuery = useClientsList();
  const salesUsersQuery = useOfferSalesUsers();
  const createOffer = useCreateOffer();
  const { mutateAsync: addUnits } = useAddUnitsToOffer();
  const offerForm = useForm<CreateOfferFormValues>({
    // Seeded once from the calendar-driven quick-add handoff (CU-86cb8grgn):
    // media unit selection, term, and creating-mode form data carried through
    // "Otwórz pełny formularz" via `/offers/new` search params instead of
    // being lost. `useForm`'s `defaultValues` is only read at mount, matching
    // this one-time restore.
    defaultValues: buildDefaultValuesFromSearch(search),
    resolver: zodResolver(CreateOfferSchema) as Resolver<CreateOfferFormValues>,
  });
  const selectedClientId = useWatch({ control: offerForm.control, name: 'clientId' });
  const salespersonId = useWatch({ control: offerForm.control, name: 'salespersonId' });
  const brand = useWatch({ control: offerForm.control, name: 'brand' });

  const authUserId = extractAuthUserId(auth);
  const salesUsers = React.useMemo(() => salesUsersQuery.data ?? [], [salesUsersQuery.data]);
  const canAssignOtherSalesperson = useHasPermission('offer.assign_other_salesperson');
  const { isSalesDepartment, submitDisabled, fieldDisabled } = resolveSalespersonGating(
    salesUsers,
    authUserId,
    salespersonId,
    createOffer.isPending,
    canAssignOtherSalesperson,
  );

  const baseClients = React.useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const { clients, handleClientCreated } = useInlineClientCreate<Client>(baseClients);
  const selectedClient = React.useMemo(
    () => clients.find((client) => client.id === selectedClientId),
    [clients, selectedClientId],
  );
  const selectedSalesperson = React.useMemo(
    () => salesUsers.find((user) => user.id === salespersonId),
    [salesUsers, salespersonId],
  );
  // See computeCampaignNumberPreviewInput's own doc comment — null until a
  // client and a salesperson are both selected, and never pairs the
  // currently selected client with a brand it doesn't have, even for the
  // one render right after switching clients where `brand` still holds the
  // previous client's value (the field-clearing effect below runs after).
  const campaignNumberPreviewInput = React.useMemo(
    () =>
      computeCampaignNumberPreviewInput({
        clientId: selectedClient?.id ?? null,
        clientAcronym: selectedClient?.acronym ?? null,
        clientBrands: selectedClient?.brands,
        brand,
        salespersonName: selectedSalesperson?.name ?? null,
      }),
    [selectedClient, selectedSalesperson, brand],
  );
  const campaignNumberPreview = useCampaignNumberPreview(campaignNumberPreviewInput);

  React.useEffect(() => {
    const currentBrand = offerForm.getValues('brand');
    if (isBrandStillValid(currentBrand, selectedClient?.brands)) return;
    offerForm.setValue('brand', null, { shouldDirty: true, shouldValidate: true });
  }, [offerForm, selectedClient]);

  /**
   * Called when the user creates a new client via `ClientDialog` or selects
   * an existing one from suggestions. The client is merged into `clients`
   * (via `useInlineClientCreate`) and auto-selected here. When `wasCreated`
   * is true (an actual inline creation, not picking an existing suggestion)
   * and the new client has exactly one brand, that brand is auto-selected
   * too — see `applyAutoSelectedBrand`'s own doc comment.
   */
  function handleClientSelected(newClient: Client, wasCreated: boolean) {
    handleClientCreated(newClient);
    offerForm.setValue('clientId', newClient.id, { shouldDirty: true, shouldValidate: true });
    offerForm.clearErrors('clientId');
    applyAutoSelectedBrand(newClient.brands, wasCreated, (brand) =>
      offerForm.setValue('brand', brand, { shouldDirty: true, shouldValidate: true }),
    );
  }

  const { action: createClientAction, dialog: createClientDialog } = useInlineClientCreateAction({
    canCreateClient,
    onClientCreated: handleClientSelected,
  });

  useSalespersonSelfDefault(offerForm, isSalesDepartment, authUserId);

  const [addCoCreators, setAddCoCreators] = React.useState(false);
  // Set once the offer is created when addCoCreators was checked, so the
  // SharingDialog renders against the new offer's id before navigating away
  // (offer-client-collaboration, CU-86cbc2qv8). Navigation is deferred to
  // the dialog's onClose so "immediately open" reads as a blocking step, not
  // a race with the offer-detail navigation.
  const [sharingDialogOfferId, setSharingDialogOfferId] = React.useState<string | null>(null);

  async function navigateToNewOffer(offerId: number) {
    await navigate({
      to: '/offers/$offerId',
      params: { offerId: String(offerId) },
      search: { tab: 'units' },
    });
  }

  async function handleCreateOffer(data: CreateOfferFormValues) {
    try {
      const offer = await createOffer.mutateAsync({
        ...(data.brand ? { brand: data.brand } : {}),
        ...(data.brandingMode ? { brandingMode: data.brandingMode } : {}),
        ...(data.defaultDateFrom ? { defaultDateFrom: data.defaultDateFrom } : {}),
        ...(data.defaultDateTo ? { defaultDateTo: data.defaultDateTo } : {}),
        ...(data.validUntil ? { validUntil: data.validUntil } : {}),
        ...(data.salespersonId ? { salespersonId: data.salespersonId } : {}),
        clientId: data.clientId,
      });
      await addQuickAddUnitsBestEffort(addUnits, offer.id, offer.title, mediaUnitIds, t, tCommon);
      if (addCoCreators) {
        setSharingDialogOfferId(String(offer.id));
        return;
      }
      await navigateToNewOffer(offer.id);
    } catch (error) {
      handleCreateOfferError(error, offerForm, t);
    }
  }

  async function handleSharingDialogClose() {
    const offerId = sharingDialogOfferId;
    setSharingDialogOfferId(null);
    if (offerId != null) {
      await navigateToNewOffer(Number(offerId));
    }
  }

  if (clientsQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">{t('create.loading')}</div>;
  }

  if (clientsQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm text-destructive">{t('create.loadError')}</p>
        <Button type="button" size="sm" onClick={() => void clientsQuery.refetch()}>
          {t('create.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('create.title')}</CardTitle>
          <CardDescription>{t('create.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={offerForm.handleSubmit(handleCreateOffer)}>
            <div className="space-y-1.5" data-testid="field-client">
              <Label htmlFor="create-offer-client" required>
                {t('create.fields.client')}
              </Label>
              <Controller
                control={offerForm.control}
                name="clientId"
                render={({ field }) => (
                  <SearchableSelectField
                    data-testid="create-offer-client"
                    disabled={createOffer.isPending}
                    error={Boolean(offerForm.formState.errors.clientId)}
                    options={clients.map((client) => ({
                      label: client.name,
                      value: String(client.id),
                    }))}
                    placeholder={t('create.fields.clientPlaceholder')}
                    value={field.value != null ? String(field.value) : ''}
                    onChange={(value) => {
                      field.onChange(Number(value));
                      offerForm.clearErrors('clientId');
                    }}
                    action={createClientAction}
                  />
                )}
              />
              {offerForm.formState.errors.clientId?.message ? (
                <p className="text-xs text-destructive" data-testid="field-client-error">
                  {getOfferFieldErrorMessage(
                    'clientId',
                    offerForm.formState.errors.clientId.message,
                    t,
                  )}
                </p>
              ) : null}
            </div>
            {createClientDialog}

            <Controller
              control={offerForm.control}
              name="brand"
              render={({ field }) => (
                <BrandField
                  brandValue={field.value}
                  onBrandChange={field.onChange}
                  brandError={
                    offerForm.formState.errors.brand?.message
                      ? getOfferFieldErrorMessage(
                          'brand',
                          offerForm.formState.errors.brand.message,
                          t,
                        )
                      : undefined
                  }
                  selectedClientBrands={selectedClient?.brands ?? []}
                  disabled={createOffer.isPending}
                />
              )}
            />

            <SalespersonField
              offerForm={offerForm}
              salesUsers={salesUsers}
              disabled={createOffer.isPending}
              fieldLocked={fieldDisabled}
              t={t}
            />

            <CampaignNumberPreviewField preview={campaignNumberPreview} />

            <DraftDefaultsSection form={offerForm} disabled={createOffer.isPending} t={t} />

            <CoCreatorsCheckbox
              checked={addCoCreators}
              onCheckedChange={setAddCoCreators}
              disabled={createOffer.isPending}
              testId="create-offer-co-creators"
            />

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => void navigate({ to: '/offers' })}
              >
                {t('create.actions.cancel')}
              </Button>
              <Button type="submit" disabled={submitDisabled}>
                {t('create.actions.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {sharingDialogOfferId ? (
        <SharingDialog
          isOpen
          offerId={sharingDialogOfferId}
          onClose={() => void handleSharingDialogClose()}
        />
      ) : null}
    </div>
  );
}
